import { useEffect, useState } from "react";
import { Plus, Search, X } from "lucide-react";

export function PageHeader({ overline, title, action }) {
  return (
    <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
      <div>
        <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#4A5568]">{overline}</div>
        <h1 className="font-display text-[28px] font-bold tracking-tight leading-none mt-1.5">{title}</h1>
      </div>
      {action}
    </div>
  );
}

export function PrimaryButton({ children, onClick, type = "button", testid, disabled }) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className="btn-tactile bg-[#1F4E2C] text-white px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#0A1128] transition-colors flex items-center gap-2 disabled:opacity-60"
    >
      {children}
    </button>
  );
}

export function GhostButton({ children, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="border border-[#0A1128] px-4 py-2 font-mono text-xs uppercase tracking-widest hover:bg-[#0A1128] hover:text-white transition-colors"
    >
      {children}
    </button>
  );
}

export function Modal({ open, onClose, title, children, wide }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className={"bg-white border border-[#E2E4DC] " + (wide ? "max-w-3xl" : "max-w-lg") + " w-full max-h-[90vh] overflow-y-auto"}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E2E4DC]">
          <div className="font-display text-xl font-bold">{title}</div>
          <button onClick={onClose} className="text-[#4A5568] hover:text-[#0A1128]" data-testid="modal-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

export function Field({ label, children, className = "" }) {
  return (
    <label className={"block " + className}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-[#4A5568] mb-1.5">{label}</div>
      {children}
    </label>
  );
}

export function TextInput(props) {
  return (
    <input
      {...props}
      className={
        "w-full bg-white border border-[#E2E4DC] focus:border-[#1F4E2C] outline-none px-3 py-2.5 text-sm font-mono " +
        (props.className || "")
      }
    />
  );
}

export function Select({ children, ...props }) {
  return (
    <select
      {...props}
      className={
        "w-full bg-white border border-[#E2E4DC] focus:border-[#1F4E2C] outline-none px-3 py-2.5 text-sm font-mono " +
        (props.className || "")
      }
    >
      {children}
    </select>
  );
}

export function Textarea(props) {
  return (
    <textarea
      {...props}
      className={
        "w-full bg-white border border-[#E2E4DC] focus:border-[#1F4E2C] outline-none px-3 py-2 text-sm font-mono " +
        (props.className || "")
      }
    />
  );
}

export function PriorityBadge({ value }) {
  const map = {
    premium: "bg-[#0A1128] text-white",
    moderate: "border border-[#0A1128] text-[#0A1128]",
    low: "border border-[#CBD5E1] text-[#64748B]",
  };
  return (
    <span className={"px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest " + map[value]}>{value}</span>
  );
}

export function StatusPill({ value, tone = "default" }) {
  const tones = {
    default: "border border-[#E2E4DC] text-[#0A1128]",
    success: "bg-emerald-100 text-emerald-800",
    warning: "bg-amber-100 text-amber-800",
    danger: "bg-red-100 text-red-700",
    info: "bg-sky-100 text-sky-800",
    muted: "bg-slate-100 text-slate-600",
  };
  return (
    <span className={"px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest " + tones[tone]}>{value}</span>
  );
}

export function ActivityTypeBadge({ type }) {
  const map = {
    appointment: "bg-blue-100 text-blue-800",
    documentation: "bg-purple-100 text-purple-800",
    verification: "bg-amber-100 text-amber-800",
    follow_up: "bg-slate-200 text-slate-800",
    approval: "bg-emerald-100 text-emerald-800",
    task: "bg-indigo-100 text-indigo-800",
    execution: "bg-orange-100 text-orange-800",
    closure: "bg-green-100 text-green-800",
    ticket: "bg-rose-100 text-rose-800",
  };
  return (
    <span className={"px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest " + (map[type] || "bg-slate-100")}>
      {type.replace("_", " ")}
    </span>
  );
}

export function SearchBar({ value, onChange, placeholder = "Search…" }) {
  return (
    <div className="flex items-center gap-2 border border-[#E2E4DC] bg-white px-3 py-2 max-w-md">
      <Search className="w-4 h-4 text-[#4A5568]" />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="bg-transparent outline-none text-sm font-mono w-full"
        data-testid="search-input"
      />
    </div>
  );
}

export function useFetch(path, deps = []) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    import("../../lib/api").then(({ default: api }) =>
      api.get(path).then((r) => alive && setData(r.data)).finally(() => alive && setLoading(false))
    );
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, reloadKey]);
  return { data, loading, reload: () => setReloadKey((k) => k + 1), setData };
}


// Lightweight pagination footer used by Leads & Opportunities tables.
// Renders only when there's more than one page; otherwise stays out of the way.
export function Pagination({ page, pageSize, total, onPageChange, onPageSizeChange, testid = "pagination" }) {
  const totalPages = Math.max(1, Math.ceil((total || 0) / (pageSize || 1)));
  const safePage = Math.min(page, totalPages);
  const start = total === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const end = Math.min(safePage * pageSize, total);
  return (
    <div
      className="px-4 py-3 border-t border-[#DDE5D8] bg-[#F4F6F1] flex items-center justify-between gap-3 flex-wrap"
      data-testid={testid}
    >
      <div className="flex items-center gap-2">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#475569]">Rows / page</span>
        <select
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          data-testid={`${testid}-size`}
          className="text-xs font-mono border border-[#DDE5D8] px-2 py-1 bg-white focus:border-[#1F4E2C] outline-none"
        >
          {[10, 20, 50, 100].map((n) => (
            <option key={n} value={n}>{n}</option>
          ))}
        </select>
      </div>

      <div className="font-mono text-[11px] text-[#475569]" data-testid={`${testid}-summary`}>
        <span className="text-[#0F1A12] font-semibold">{start}-{end}</span>
        <span className="text-[#94A3B8]"> of </span>
        <span className="text-[#0F1A12] font-semibold">{total}</span>
      </div>

      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onPageChange(Math.max(1, safePage - 1))}
          disabled={safePage <= 1}
          data-testid={`${testid}-prev`}
          className="text-[10px] font-mono uppercase tracking-widest px-3 py-1.5 border border-[#DDE5D8] text-[#475569] hover:border-[#1F4E2C] hover:text-[#1F4E2C] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          ← Previous
        </button>
        <span className="font-mono text-[11px] text-[#0F1A12] px-3 py-1.5" data-testid={`${testid}-page`}>
          Page <span className="font-semibold">{safePage}</span> of <span className="font-semibold">{totalPages}</span>
        </span>
        <button
          type="button"
          onClick={() => onPageChange(Math.min(totalPages, safePage + 1))}
          disabled={safePage >= totalPages}
          data-testid={`${testid}-next`}
          className="text-[10px] font-mono uppercase tracking-widest px-3 py-1.5 border border-[#DDE5D8] text-[#475569] hover:border-[#1F4E2C] hover:text-[#1F4E2C] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          Next →
        </button>
      </div>
    </div>
  );
}
